import {
  PLAN_IDS,
  PLAN_LABEL,
  createTenantSchema,
  type EntitlementKey,
  type PlanId,
  type TenantAdminView,
} from '@agents/shared';
import { useMemo, useState, type FormEvent } from 'react';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  FormDialog,
  OverflowMenu,
  PageHeader,
  PersonAvatar,
  problemMessage,
  SearchInput,
  SkeletonRows,
  StatusPill,
  TableSkeleton,
  useToast,
} from '../components/ui';
import { ApiProblem, tenantSelection } from '../lib/api';
import { useDocumentTitle } from '../lib/document-title';
import { clearError } from '../lib/forms';
import { dateTime, relativeTime, slugify } from '../lib/format';
import {
  useCreateTenant,
  useSetTenantOverrides,
  useSetTenantPlan,
  useTenantEntitlements,
  useTenants,
} from '../lib/queries';

function CreateTenantDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = useCreateTenant();
  const toast = useToast();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const close = (next: boolean) => {
    if (!next) {
      setName('');
      setSlug('');
      setSlugTouched(false);
      setAdminEmail('');
      setErrors({});
    }
    onOpenChange(next);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = createTenantSchema.safeParse({ name, slug, adminEmail });
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [i.path.join('.'), i.message])));
      return;
    }
    setErrors({});
    try {
      const t = await create.mutateAsync(parsed.data);
      toast(`Espacio ${t.name} creado`, { detail: `${parsed.data.adminEmail} es su administrador.` });
      close(false);
    } catch (err) {
      if (err instanceof ApiProblem) {
        const fields = err.fieldErrors();
        setErrors(Object.keys(fields).length ? fields : { slug: err.problem.detail ?? err.problem.title });
      }
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={close}
      title="Crear espacio"
      description="El administrador tendrá acceso en cuanto inicie sesión con su correo."
      submitLabel="Crear espacio"
      onSubmit={submit}
      loading={create.isPending}
    >
      <Field label="Nombre del cliente" error={errors.name}>
        {(p) => (
          <input
            {...p}
            className="input"
            placeholder="Ej.: Ferretería El Tornillo"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setErrors(clearError('name'));
              if (!slugTouched) setSlug(slugify(e.target.value));
            }}
          />
        )}
      </Field>
      <Field label="Identificador" help="Único y permanente. Minúsculas y guiones." error={errors.slug}>
        {(p) => (
          <input
            {...p}
            className="input mono"
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
              setErrors(clearError('slug'));
            }}
          />
        )}
      </Field>
      <Field label="Correo del administrador" error={errors.adminEmail}>
        {(p) => (
          <input
            {...p}
            className="input"
            type="email"
            placeholder="nombre@empresa.com"
            value={adminEmail}
            onChange={(e) => {
              setAdminEmail(e.target.value);
              setErrors(clearError('adminEmail'));
            }}
          />
        )}
      </Field>
    </FormDialog>
  );
}

const LIMIT_FIELDS: { key: EntitlementKey; label: string; unit: string }[] = [
  { key: 'knowledge.storageMb', label: 'Almacenamiento', unit: 'MB' },
  { key: 'knowledge.processedMbPerMonth', label: 'Procesado por mes', unit: 'MB' },
  { key: 'knowledge.maxFileMb', label: 'Tamaño máximo por archivo', unit: 'MB' },
  { key: 'knowledge.maxDocuments', label: 'Documentos', unit: '' },
  { key: 'knowledge.url.maxPagesPerSource', label: 'Páginas por sitio web', unit: '' },
  { key: 'knowledge.api.maxConnectors', label: 'Conectores API', unit: '' },
  { key: 'knowledge.mcp.maxServers', label: 'Servidores MCP', unit: '' },
];

const numberFmt = new Intl.NumberFormat('es');

function LimitsDialog({ tenant, onClose }: { tenant: TenantAdminView | null; onClose: () => void }) {
  const current = useTenantEntitlements(tenant?.id ?? null);
  const save = useSetTenantOverrides();
  const toast = useToast();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  const data = current.data;
  const planValues = data ? data.plans[data.plan] : null;

  const value = (key: EntitlementKey) =>
    key in draft ? draft[key]! : data?.overridden.includes(key) ? String(data.values[key]) : '';

  const close = () => {
    setDraft({});
    setReason('');
    setError(undefined);
    onClose();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenant || !data) return;
    const overrides: Record<string, number | null> = {};
    for (const f of LIMIT_FIELDS) {
      if (!(f.key in draft)) continue;
      const raw = draft[f.key]!.trim();
      overrides[f.key] = raw === '' ? null : Number(raw);
    }
    if (!Object.keys(overrides).length) return close();
    if (reason.trim().length < 3) {
      setError('Explica el motivo del ajuste.');
      return;
    }
    try {
      await save.mutateAsync({ tenantId: tenant.id, reason, overrides });
      toast(`Límites de ${tenant.name} actualizados`);
      close();
    } catch (err) {
      setError(problemMessage(err, 'No se pudieron guardar los límites. Inténtalo de nuevo.'));
    }
  };

  return (
    <FormDialog
      open={!!tenant}
      onOpenChange={(o) => !o && close()}
      size="lg"
      title={`Límites de ${tenant?.name ?? ''}`}
      description={`Deja un campo vacío para usar el valor del plan${data ? ` ${PLAN_LABEL[data.plan]}` : ''}. Los ajustes quedan auditados.`}
      submitLabel="Guardar límites"
      onSubmit={submit}
      loading={save.isPending}
    >
      {current.isPending && <SkeletonRows rows={4} />}
      {current.isError && <ErrorState error={current.error} onRetry={() => void current.refetch()} />}
      {data && planValues && (
        <>
          <div className="row">
            {LIMIT_FIELDS.map((f) => (
              <Field
                key={f.key}
                label={f.label}
                help={`Plan: ${numberFmt.format(Number(planValues[f.key]))}${f.unit ? ` ${f.unit}` : ''}`}
              >
                {(p) => (
                  <input
                    {...p}
                    className="input"
                    type="number"
                    min={0}
                    inputMode="numeric"
                    placeholder={String(planValues[f.key])}
                    value={value(f.key)}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  />
                )}
              </Field>
            ))}
          </div>
          <Field label="Motivo" error={error}>
            {(p) => (
              <input
                {...p}
                className="input"
                value={reason}
                placeholder="Ej.: piloto con más almacenamiento hasta diciembre"
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
        </>
      )}
    </FormDialog>
  );
}

const STATUS: Record<
  TenantAdminView['status'],
  { label: string; tone?: 'success' | 'warning' | 'danger' | 'info' }
> = {
  active: { label: 'Activo', tone: 'success' },
  provisioning: { label: 'Preparando', tone: 'info' },
  suspended: { label: 'Suspendido', tone: 'warning' },
  failed: { label: 'Falló la preparación', tone: 'danger' },
};

export function TenantsPage() {
  useDocumentTitle('Espacios');
  const tenants = useTenants();
  const setPlan = useSetTenantPlan();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState('');
  const [planChange, setPlanChange] = useState<{ tenant: TenantAdminView; plan: PlanId } | null>(null);
  const [limitsFor, setLimitsFor] = useState<TenantAdminView | null>(null);

  const items = useMemo(() => tenants.data ?? [], [tenants.data]);
  const q = query.trim().toLowerCase();
  const visible = items.filter((t) => !q || `${t.name} ${t.slug}`.toLowerCase().includes(q));

  const confirmPlan = async () => {
    if (!planChange) return;
    try {
      await setPlan.mutateAsync({ tenantId: planChange.tenant.id, plan: planChange.plan });
      toast(`${planChange.tenant.name} ahora está en el plan ${PLAN_LABEL[planChange.plan]}`);
    } catch (err) {
      toast('No se pudo cambiar el plan', {
        tone: 'danger',
        detail: problemMessage(err, 'Inténtalo de nuevo.'),
      });
    } finally {
      setPlanChange(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Espacios"
        description="Cada cliente de Stratika opera en un espacio aislado, con sus propios agentes, ejecuciones y equipo."
        actions={
          <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
            Crear espacio
          </Button>
        }
      />
      {tenants.isPending && <TableSkeleton rows={4} cols={5} />}
      {tenants.isError && <ErrorState error={tenants.error} onRetry={() => void tenants.refetch()} />}
      {tenants.data?.length === 0 && (
        <EmptyState
          icon="spaces"
          title="Aún no hay espacios"
          action={
            <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
              Crear espacio
            </Button>
          }
        >
          Crea un espacio por cliente. Su administrador podrá entrar e invitar a su equipo.
        </EmptyState>
      )}
      {items.length > 0 && (
        <>
          <div className="toolbar">
            <SearchInput
              label="Buscar espacios"
              placeholder="Buscar por nombre o identificador"
              value={query}
              onChange={setQuery}
            />
            <span className="count">
              {visible.length === items.length
                ? `${items.length} en total`
                : `${visible.length} de ${items.length}`}
            </span>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Espacio</th>
                  <th scope="col" data-optional>
                    Estado
                  </th>
                  <th scope="col">Plan</th>
                  <th scope="col" className="num" data-optional>
                    Miembros
                  </th>
                  <th scope="col" data-optional>
                    Creado
                  </th>
                  <th scope="col">
                    <span className="visually-hidden">Acciones</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <div className="cell-main">
                        <PersonAvatar name={t.name} square />
                        <div>
                          <span className="name">{t.name}</span>
                          <small className="mono">{t.slug}</small>
                        </div>
                      </div>
                    </td>
                    <td data-optional>
                      <StatusPill tone={STATUS[t.status]?.tone}>
                        {STATUS[t.status]?.label ?? t.status}
                      </StatusPill>
                    </td>
                    <td>
                      <select
                        className="select select-sm"
                        aria-label={`Plan de ${t.name}`}
                        value={t.plan}
                        onChange={(e) => setPlanChange({ tenant: t, plan: e.target.value as PlanId })}
                      >
                        {PLAN_IDS.map((p) => (
                          <option key={p} value={p}>
                            {PLAN_LABEL[p]}
                          </option>
                        ))}
                      </select>
                      {t.overrideCount > 0 && (
                        <small className="muted" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                          {t.overrideCount} {t.overrideCount === 1 ? 'límite ajustado' : 'límites ajustados'}
                        </small>
                      )}
                    </td>
                    <td className="num" data-optional>
                      {t.memberCount}
                    </td>
                    <td className="muted nowrap" data-optional title={dateTime(t.createdAt)}>
                      {relativeTime(t.createdAt)}
                    </td>
                    <td className="actions-cell">
                      <Button
                        size="sm"
                        onClick={() => {
                          tenantSelection.set(t.id);
                          window.location.assign('/agentes');
                        }}
                      >
                        Entrar
                      </Button>
                      <OverflowMenu
                        size="sm"
                        label={`Más acciones para ${t.name}`}
                        actions={[
                          { label: 'Ajustar límites', icon: 'sliders', onSelect: () => setLimitsFor(t) },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
                {!visible.length && (
                  <tr className="no-results">
                    <td colSpan={6}>Ningún espacio coincide con la búsqueda.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      <CreateTenantDialog open={creating} onOpenChange={setCreating} />
      <ConfirmDialog
        open={!!planChange}
        onOpenChange={(o) => !o && setPlanChange(null)}
        tone="primary"
        title={planChange ? `¿Cambiar ${planChange.tenant.name} al plan ${PLAN_LABEL[planChange.plan]}?` : ''}
        description="Los límites del nuevo plan aplican de inmediato. Los ajustes por cliente se conservan."
        confirmLabel="Cambiar plan"
        onConfirm={() => void confirmPlan()}
        loading={setPlan.isPending}
      />
      <LimitsDialog tenant={limitsFor} onClose={() => setLimitsFor(null)} />
    </>
  );
}
